import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Calculator, 
  CreditCard, 
  Shield, 
  Clock, 
  CheckCircle,
  Info,
  ExternalLink,
  Bath
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FinanceSEOPage = ({ location = "Manchester" }) => {
  const [projectCost, setProjectCost] = useState(6000);
  const [term, setTerm] = useState(36);
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  const APR = 9.9;

  useEffect(() => {
    const monthlyRate = APR / 100 / 12;
    const payment = (projectCost * monthlyRate * Math.pow(1 + monthlyRate, term)) / 
                    (Math.pow(1 + monthlyRate, term) - 1);
    setMonthlyPayment(Math.round(payment * 100) / 100);
  }, [projectCost, term]);

  const termOptions = [
    { value: 12, label: "12 months" },
    { value: 24, label: "24 months" },
    { value: 36, label: "36 months" },
    { value: 48, label: "48 months" }
  ];

  const faqs = [
    { q: "Can I finance my bathroom renovation?", a: "Yes! We offer flexible finance options for bathroom refits from £500 to £20,000, allowing you to spread the cost over 12-48 months." },
    { q: "What credit score do I need?", a: "Our finance partners consider all applications. Even if you have less than perfect credit, you may still be approved." },
    { q: "How quickly can I get a decision?", a: "Most applications receive a decision within minutes. Once approved, we can start planning your project right away." },
    { q: "Is there a deposit required?", a: "Finance options are available with various deposit levels, including 0% deposit options subject to status." }
  ];

  const handleApplyFinance = () => {
    window.open("https://kanda.co.uk/apply", "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid={`finance-seo-${location.toLowerCase()}`}>
      {/* Hero */}
      <section className="seo-hero">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full mb-6">
                <CreditCard className="w-4 h-4" />
                <span className="text-sm font-medium">Flexible Finance Available</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat">
                Bathroom Finance in {location}
              </h1>
              <p className="text-lg text-white/80 mb-8">
                Spread the cost of your bathroom renovation with affordable monthly payments. 
                Finance options from £500 to £20,000 available in {location} and across Greater Manchester.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#calculator">
                  <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="finance-seo-calculator-cta">
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate Payments
                  </Button>
                </a>
                <Link to="/bathroom-refit">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                    Get Bathroom Quote
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1758448018619-4cbe2250b9ad?w=800" 
                  alt={`Bathroom renovation finance in ${location}`} 
                  className="w-full h-full object-cover" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: CreditCard, title: "Spread the Cost", description: "Monthly instalments" },
              { icon: Clock, title: "Quick Decision", description: "Minutes, not days" },
              { icon: Shield, title: "FCA Regulated", description: "Secure & trusted" },
              { icon: CheckCircle, title: "Flexible Terms", description: "12-48 months" }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Bathroom Finance Calculator
              </h2>
              <p className="text-lg text-gray-600">
                See how affordable your {location} bathroom renovation could be
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              {/* Project Cost Slider */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-medium">Project Cost</Label>
                  <span className="text-2xl font-bold text-primary">£{projectCost.toLocaleString()}</span>
                </div>
                <Slider
                  value={[projectCost]}
                  onValueChange={(value) => setProjectCost(value[0])}
                  min={500}
                  max={20000}
                  step={100}
                  className="mb-2"
                  data-testid="seo-finance-cost-slider"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>£500</span>
                  <span>£20,000</span>
                </div>
              </div>

              {/* Term Selection */}
              <div className="mb-8">
                <Label className="text-base font-medium mb-4 block">Finance Term</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {termOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTerm(option.value)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        term === option.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-gray-200 hover:border-primary/50"
                      }`}
                    >
                      <span className="font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Monthly Payment Result */}
              <div className="bg-gradient-to-br from-primary to-blue-700 rounded-xl p-8 text-center text-white mb-6">
                <p className="text-white/80 mb-2">Estimated Monthly Payment</p>
                <p className="text-5xl font-bold mb-2" data-testid="seo-monthly-payment">
                  £{monthlyPayment.toLocaleString()}
                </p>
                <p className="text-white/70 text-sm">per month for {term} months</p>
              </div>

              {/* Buttons */}
              <div className="grid md:grid-cols-2 gap-4">
                <Button 
                  className="bg-primary hover:bg-primary/90 py-6"
                  onClick={handleApplyFinance}
                  data-testid="seo-apply-finance"
                >
                  Apply For Finance
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/bathroom-refit">
                  <Button variant="outline" className="w-full border-2 border-primary text-primary hover:bg-primary/5 py-6">
                    Get Bathroom Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Disclaimer */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Finance subject to status and approval. Terms and conditions apply. 
                    Finance is provided by a regulated third-party lender.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Examples */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
              Bathroom Refit Prices in {location}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Small Bathroom</h3>
              <p className="text-3xl font-bold text-primary mb-1">£3,500 - £5,500</p>
              <p className="text-sm text-gray-500 mb-4">from £105/month*</p>
              <Link to="/bathroom-refit">
                <Button variant="outline" size="sm" className="border-primary text-primary">
                  Get Quote
                </Button>
              </Link>
            </div>
            <div className="bg-primary/10 rounded-xl p-6 border-2 border-primary text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Medium Bathroom</h3>
              <p className="text-3xl font-bold text-primary mb-1">£5,500 - £8,500</p>
              <p className="text-sm text-gray-500 mb-4">from £165/month*</p>
              <Link to="/bathroom-refit">
                <Button size="sm" className="bg-primary hover:bg-primary/90">
                  Get Quote
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Large Bathroom</h3>
              <p className="text-3xl font-bold text-primary mb-1">£8,500 - £12,000</p>
              <p className="text-sm text-gray-500 mb-4">from £255/month*</p>
              <Link to="/bathroom-refit">
                <Button variant="outline" size="sm" className="border-primary text-primary">
                  Get Quote
                </Button>
              </Link>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            *Based on 9.9% APR over 36 months. Representative example only.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center font-montserrat">
            Bathroom Finance FAQs
          </h2>
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible>
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready to Transform Your {location} Bathroom?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get your bathroom refit estimate and explore finance options today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/bathroom-refit">
              <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                Estimate My Bathroom Refit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10"
              onClick={handleApplyFinance}
            >
              Apply For Finance
              <ExternalLink className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-600">
              <strong>Finance Disclaimer:</strong> Finance is subject to status and approval. 
              Terms and conditions apply. Finance is provided by a regulated third-party lender. 
              DC Tiling acts as a credit intermediary and not a lender.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinanceSEOPage;
